import { Component, Host, AfterViewInit, ViewChild, Optional, SkipSelf } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { OrganizationService } from '../../services/organization.service';
import { SubHeaderService } from '../../../core/services/sub-header.service';
import { SubHeader } from "../../../core/models/sub-header.model";
import { Contact, AgentType } from '../../../admin/models/contact.model';
import { Organization, OrganizationComplianceDetails, FinancialComplianceSearchSpec, OperationType, OrganizationEvent, OrganizationEventType } from '../../models/organization.model';
import { LoaderService } from '../../services/loader.service';
import { SidePanel } from "../../models/side-panel.model";
import { SidePanelService } from "../../services/side-panel.service";
import { Address } from '../../models/address.model';
import { AuthService } from '../../../services/auth.service';
import { OrganizationWrapperComponent } from './components/organization-wrapper/organization-wrapper.component';
import { OrgContactEditorModalComponent } from "./org-contact-editor-modal.component";
import { OrganizationContactsComponent } from "./components/organization-contacts.component";
import { LunaClaimValues } from '../../../models/user-identity.model';
import { Subscription, BehaviorSubject, Observable, of, forkJoin } from 'rxjs';
import { tap, finalize, map, switchMap } from "rxjs/operators";
import { Note } from "../../models/note.model";
import { CriticalNoteService } from "../../services/critical-note.service";
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { CanDeactivateComponent } from "../../../common/classes/can-deactivate.component";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { ContactService } from "../../../admin/services/contacts.service";

@Component({
    selector: 'app-organization-detail',
    templateUrl: './organization-detail.component.html',
    styleUrls: ['./organization-detail.component.scss'],
    standalone: false
})
export class OrganizationDetailComponent extends CanDeactivateComponent implements AfterViewInit {

	@ViewChild(OrgContactEditorModalComponent) editor: OrgContactEditorModalComponent;
	@ViewChild('activeContacts') activeContacts: OrganizationContactsComponent;

    blankAddress: Address = {
        street: '',
        suite: '',
        poBox: '',
        city: '',
        stateCode: '',
        zipCode: '',
    };

    isRecalculating: boolean = false;
    calendarTabActive: boolean = false;

    onTabClicked(tab: any): void {
        this.calendarTabActive = tab.title === 'Calendar';
    }

	loadingCompliance: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

	get canEdit(): boolean {
		return this.auth.hasSystemPermission(LunaClaimValues.ManageAnyOrganization) != null;
	}

    get complianceIsLoading(): boolean {
        return this.loadingCompliance.value;
	}

	private _fieldUnderEdit: string = "";

	get fieldUnderEdit(): string {
		return this._fieldUnderEdit;
	}

	editForm: FormGroup = new FormGroup({});

	updatesCompleteDialog: Dialog = new Dialog("Organization has been updated.", [new DialogButton("OK", false, () => { return false; })]);
	noUpdatesDialog: Dialog = new Dialog("No updates have been made to the organization to save.", [new DialogButton("OK", false, () => { return false; })]);

	updatedContacts: Contact[] = [];

    org: Organization = null;
    organizations: Organization[];
    _emergencyContact: Contact;
	_primaryContact: Contact;

	get primaryContactExists(): boolean {
		return this.primaryContact != null && this.primaryContact.id != null && this.primaryContact.id.length > 0;
	}

	get emergencyContactExists(): boolean {
		return this.emergencyContact != null && this.emergencyContact.id != null && this.emergencyContact.id.length > 0;
	}

	get primaryContact(): Contact {
		return this._primaryContact;
	}

	set primaryContact(value: Contact) {
		this._primaryContact = value;
	}

	get emergencyContact(): Contact {
		return this._emergencyContact;
	}

	set emergencyContact(value: Contact) {
		this._emergencyContact = value;
	}	


	displayAddContactButton: boolean;


    private complianceDetails: OrganizationComplianceDetails;

    get orgCompliance(): OrganizationComplianceDetails {
        return this.complianceDetails;
    }

    set orgCompliance(value: OrganizationComplianceDetails) {
        this.complianceDetails = value;
    }

    get organizationNotes(): Note[] {
        if (this.org == null) return [];
        if (this.org.notes == null) this.org.notes = [];
        return this.org.notes;
	}

	private _organizationStatus: any = undefined;
	public loadingStatuses: boolean = true;

	get organizationStatus(): string | null {
		if (this.org == null || this.statusValues == null || this.statusValues.length == 0) return null;
		this._organizationStatus = this.statusValues.find(v => v.Code === this.org.status);
		if (this._organizationStatus == null) this._organizationStatus = this.org.status;	
		return (this._organizationStatus != null && this._organizationStatus.Definition != null) ? this._organizationStatus.Definition : this._organizationStatus;
	}

	get ricsCancellationDate(): Date | null {
		if (this.org == null || this.org.history == null || this.org.history.length === 0) return null;
		let cancelEvent: OrganizationEvent = this.org.history.find(o => o.eventType === OrganizationEventType.OrganizationCancelled);
		if (cancelEvent == null) return null;
		return cancelEvent.timeStamp;
	}

	get ricsCreationDate(): Date | null {
		if (this.org == null || this.org.history == null || this.org.history.length === 0) return null;
		let createEvent: OrganizationEvent = this.org.history.find(o => o.eventType === OrganizationEventType.OrganizationCreated);
		if (createEvent == null) return null;
		return createEvent.timeStamp;
	}

	statusValues: any[] = [];
    subscriptions: Subscription = new Subscription();

    public sidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Org. Notes" }, { title: "Org. History" }],
        sections: [
            { componentName: 'OrganizationNotes', componentData: { data: this.org }, },
            { componentName: 'OrganizationHistory', componentData: { data: this.org } }
        ],
        visible: false
    });

    template: string = '';

    constructor(
        public loader: LoaderService,
		authService: AuthService,
		dialogService: DynamicDialogService,
        @Optional() @SkipSelf() public _organizationWrapper: OrganizationWrapperComponent,
        public router: Router,
        public service: OrganizationService,
        public sidePanelService: SidePanelService,
        public subHeaderService: SubHeaderService,
		private criticalNoteService: CriticalNoteService,
		private _domainService: DomainDefinitionService,
		private _contactService: ContactService,
		private builder: FormBuilder	) {

		super(dialogService, authService);
        this.org = this._organizationWrapper.organization;
        this.sidePanel.sections[0].componentData.organization = this.org;
		this.sidePanel.sections[1].componentData.organization = this.org;

		if (this.org.contacts != null && this.org.contacts.length > 0) {
			let emergency = this.org.contacts.filter(c => c.agentType & AgentType.EmergencyPOC);
			if (emergency.length > 0) {
				this.emergencyContact = emergency[0];
			} else {
				this.emergencyContact = new Contact();
			}
			let primary = this.org.contacts.filter(c => c.agentType & AgentType.PrimaryContact);
			if (primary.length > 0) {
				this.primaryContact = primary[0];
			} else {
				this.primaryContact = new Contact();
			}
		}
		this.displayAddContactButton = this.org.isInternal == this.auth.User.isInternal;

		
    }

	ngOnInit() {
		this.editForm = this.buildForm();
        let spec: FinancialComplianceSearchSpec = {
            organizationIds: [],
            includeHistoricOwnerNumbers: false,
            organizationId: this.org.id,
            historicOwnerNumber: this.org.historicalOwnerNumber != null ? this.org.historicalOwnerNumber : "",
            exactMatch: true
        };
       
        let compliance$ = this.service.getComplianceData(spec).pipe(
            map((details: OrganizationComplianceDetails[]) => {
                if (details != null && details.length > 0) return details[0]; //should ever only be one entry for an organization by id since its used as primary key
                return null;
            })
        ).subscribe(
            (detail: OrganizationComplianceDetails) => {
                this.loadingCompliance.next(false);
                this.complianceDetails = detail;
            },
            (error) => { console.error(error); }
        );
        this.subscriptions.add(compliance$);
        if (this.auth.User.isInternal) {
            let adds$ = this.criticalNoteService.noteAddedObs$.subscribe(
                (note: Note) => {
                    let existingIndex = this.organizationNotes.findIndex(n => n.id === note.id);
                    if (existingIndex === -1) this.organizationNotes.unshift(note);
                },
                (error) => { console.error(error); }
            );
            this.subscriptions.add(adds$);
            let dems$ = this.criticalNoteService.noteDemotedObs$.subscribe(
                (note: Note) => {
                    let existingIndex = this.organizationNotes.findIndex(n => n.id === note.id);
                    if (existingIndex !== -1) {
                        this.organizationNotes.splice(existingIndex, 1);
                    }
                    this.organizationNotes.unshift(note);
                },
                (error) => { console.error(error); }
            );
            this.subscriptions.add(dems$);
            let dels$ = this.criticalNoteService.noteDeletedObs$.subscribe(
                (note: Note) => {
                    let existingIndex = this.organizationNotes.findIndex(n => n.id === note.id);
                    if (existingIndex !== -1) {
                        this.organizationNotes.splice(existingIndex, 1);
                    }
                },
                (error) => { console.error(error); }
            );
			this.subscriptions.add(dels$);
			let domain$ = this._domainService.getValues("CO_STAT").pipe()
				.subscribe(
					(data: any) => {
						this.statusValues = data;
						this.loadingStatuses = false;
					},
					(error) => {
						console.error(error);
					}
			);
			this.subscriptions.add(domain$);

			
		}
		this.confirmDialog = new Dialog("Any unsubmitted changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })])
    }

	ngAfterViewInit() {
		this.allowDeactivate.next(true);
		let subheader: SubHeader = {
			title: 'Organization Details',
			buttons: [{
				name: "Notes and History", method: () => this.showSidePanel()
			}]
		} as SubHeader;
		if (this._organizationWrapper != null) {
			this._organizationWrapper.updateEdit(this.canEdit);
		}
		if (this.canEdit) {
			let typeEdit$ = this._organizationWrapper._organizationService.entityUpdateObservable.subscribe(
				(type: any) => {
					let ctrl = this.editForm.get('organizationType');
					if (ctrl != null) {
						ctrl.setValue(type);
						ctrl.markAsDirty();
						this.editForm.updateValueAndValidity();						
					}
					this.org.typeCode = type;
					this.allowDeactivate.next(false);
				},
				(err) => {
					console.error(err);
				});
			this.subscriptions.add(typeEdit$);
			subheader.buttons.push({
				name: "Save",
				method: () => this.save()
			});
		}		
        this.subHeaderService.updateSubHeader(subheader);       
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    addContact(): void {
        this.router.navigate(['admin', 'contact', 'add', this.org.id])
    }

    get address(): Address {
        if (!this.org.address) {
            this.org.address = this.blankAddress;
        };
        return this.org.address;
    }

    get canStartEDiscovery(): boolean {
        if (this.auth) {
            return this.auth.hasSystemPermission(LunaClaimValues.InitiateEDiscovery) !== undefined;
        }
        return false;
    }

    get canInitiateCommunication(): boolean {
        if (this.auth) {
            let hasPermissions = this.auth.hasSystemPermission(LunaClaimValues.InitiateCommunications) !== undefined;
            return hasPermissions;
        }
        return false;
	}

	buildForm(): FormGroup {
		const phonePattern = '^\\s*$|^(\\+1\\s*)?(\\d{10}|\\(?\\d{3}\\)?[-.\\s]?\\d{3}[-.\\s]?\\d{4})$';
		return this.builder.group({
			isActive: [{ value: this.org.isActive != null && this.org.isActive, disabled: true }],
			status: [{ value: this.org.status, disabled: true }],
			organizationType: [{ value: this.org.typeCode, disabled: true }],
			address: this.builder.group({
				streetAddress: [{ value: this.org.address != null ? this.org.address.street : "", disabled: true }],
				poBox: [{ value: this.org.address != null ? this.org.address.poBox : "", disabled: true }],
				suite: [{ value: this.org.address != null ? this.org.address.suite : "", disabled: true }],
				city: [{ value: this.org.address != null ? this.org.address.city : "", disabled: true }],
				state: [{ value: this.org.address != null ? this.org.address.stateCode : "", disabled: true }],
				zipCode: [{ value: this.org.address != null ? this.org.address.zipCode : "", disabled: true }],
			}),
			primaryPhone: [{ value: this.org.phone, disabled: true }, [Validators.pattern(phonePattern)]],
			fax: [{ value: this.org.fax, disabled: true }, [Validators.pattern(phonePattern)]],
			responsiblePartyPhone: [{ value: this.org.responsiblePartyPhone, disabled: true }, Validators.pattern(phonePattern)],
			emailAddress: [{ value: this.org.email, disabled: true }, [Validators.email]]
		})
	}

    getBooleanToYesOrNo(bit: boolean): string {
        return bit ? 'Yes' : 'No';
	}

	onAddressUpdate(update: Address): void {
		this.org.address = update;
		this.allowDeactivate.next(false);
	}



    showSidePanel(): void {
        this.sidePanelService.newSidePanel(new SidePanel({
            tabs: [{ title: "Org. Notes" }, { title: "Org. History" }],
            sections: [
                { componentName: 'OrganizationNotes', componentData: { data: this.org }, },
                { componentName: 'OrganizationHistory', componentData: { data: this.org } }
            ],
            visible: false
        }));
        this.sidePanelService.toggleSidePanel(true);
    }

    get complianceStatus(): boolean {
        let complianceStatus = null;
        if (this.orgCompliance) {
            complianceStatus = (this.orgCompliance.isCompliant);
        }

        return complianceStatus;
    }

    get complianceStatusText(): string {
        let text: string = "Unavailable";
        if (this.loadingCompliance.value == true) text = "Loading Compliance...";
        else if (this.isRecalculating) text = 'Calculating...';
        else if (this.complianceStatus != null)
            text = this.complianceStatus ? 'Compliant' : 'Out of Compliance';
        return text;
    }

    get isWellOwner(): boolean {
        if (this.org == null || this.org.operationType == null) return false;
        let isOperator: boolean = (this.org.operationType & OperationType.Operator) != 0;
        let isInjectionOperator: boolean = (this.org.operationType & OperationType.InjectionOperator) != 0;
        return isOperator || isInjectionOperator;
    }

    get isBrineHauler(): boolean {
        if (this.org == null || this.org.operationType == null) return false;
        return (this.org.operationType & OperationType.BrineHauler) != 0;
	}

	editField(fieldName: string): void {
		let ctrl = this.editForm.get(fieldName);
		this._fieldUnderEdit = fieldName;
		switch (fieldName) {
			case "status":
				{
					if (ctrl != null) {
						ctrl.enable();
					}
					ctrl = this.editForm.get('isActive');
					if (ctrl != null) {
						ctrl.enable();
					}
					break;
				}
			default:
				{
					if (ctrl != null) {
						ctrl.enable();
					}					
					break;
				}
		}

		setTimeout(() => {
			let el = document.getElementById(fieldName);
			if (el != null) {
				el.focus();
			}
		}, 500);
	}

	save(): void {
		let formTouched: boolean = false;
		let validationMessages: string[] = [];
		this.editForm.enable();
		Object.keys(this.editForm.controls).forEach(key => {
			const control = this.editForm.get(key);
			if (control.disabled) {
				control.enable();
			}
			if (control.pristine == false) {
				formTouched = true;
			}
			control.updateValueAndValidity();
			if (key == 'address') {
				Object.keys((<FormGroup>control).controls).forEach(subkey => {
					const subControl = (<FormGroup>control).get(subkey);
					if (subControl != null) {
						if (subControl.disabled) {
							subControl.enable();
						}
						if (subControl.pristine == false) {
							formTouched = true;
						}
						subControl.updateValueAndValidity();
					}
				});
			}
			
			if (control.errors != null) {
				if (control.errors.email != null) {
					validationMessages.push("Provided e-mail address is in an invalid format.");
				}
				if (control.errors.pattern != null) {
					let msg: string = "";
					switch (key) {
						case "primaryPhone": {
							msg = 'Phone Number'
							break;
						}
						case "fax": {
							msg = 'Fax Number'
							break;
						}
						case "responsiblePartyPhone": {
							msg = 'Responsible Party Phone Number'
							break;
						}
					}
					msg = `${msg} is in an invalid format.`;
					validationMessages.push(msg);
				}
			}
		});
		if (formTouched == false && this.updatedContacts.length == 0) {
			this.noUpdatesDialog.toggleDialog(true);
			this.dialogService.updateDialog(this.noUpdatesDialog);
		} else {
			let org$: Observable<Organization>;
			if (formTouched) {
				this.editForm.updateValueAndValidity();
				if (this.editForm.invalid) {
					this.dialogService.showFormInvalid(validationMessages);
				} else {
					org$ = this._organizationWrapper._organizationService.save(this.org);					
				}
			} else {
				org$ = of(this.org);
			}
			let contact$: Observable<Contact[]> = of([]);
			if (this.updatedContacts && this.updatedContacts.length > 0) {
				let obsList: Observable<Contact>[] = [];
				for (let c = 0; c < this.updatedContacts.length; c++) {
					let obs: Observable<Contact> = this._contactService.save(this.updatedContacts[c]);
					obsList.push(obs);
				}
				contact$ = forkJoin(obsList);
			}
			if (this.editForm.invalid == false && (formTouched || this.updatedContacts.length > 0)) {
				this.loader.toggleLoader(true);
				let subs$ = contact$.pipe(
					tap((updates: Contact[]) => {
						if (this.activeContacts != null) {
							this.activeContacts.updateContacts(updates);
						}
					}),
					switchMap(() => {
						return org$;
					})
					).subscribe(
					() => {
							this.updatesCompleteDialog.toggleDialog(true);
							this.dialogService.updateDialog(this.updatesCompleteDialog);
							this.updatedContacts = [];
							this.allowDeactivate.next(true);
							this.editForm.markAsPristine();
							this.loader.toggleLoader(false);
					},
					(err) => {
						console.error(err);
						this.loader.toggleLoader(false);
						this.dialogService.showError();
					}
				);
				this.subscriptions.add(subs$);
			}
		}
		
	}

	onContactSelected(selected: Contact): void {
		if (this.editor != null) {
			this.editor.openForm(selected);
		}
	}

    recalculate(organization: string): void {
        if (organization == null) organization = this.org.id;
        if (this.isRecalculating === false) {
            this.isRecalculating = true;
            let detail$ = this.service.updateCompliance(organization).pipe(                
                tap((summary: OrganizationComplianceDetails) => {
                    this.complianceDetails = summary;
                }),
                finalize(() => { this.isRecalculating = false; })
            ).subscribe(
                () => { this.isRecalculating = false; },
                (error) => { 
                    console.error(error); 
                }
            );
            this.subscriptions.add(detail$);
        }      
	}

	statusChanged(evt: any): void {
		console.log('status changed', evt);
	}

	update(fieldName: string): void {
		let ctrl = this.editForm.get(fieldName);
		switch (fieldName) {
			case "status":
				{
					if (ctrl != null) {
						this.org.status = ctrl.value;
						ctrl.disable();
						switch (ctrl.value) {
							case "C": {
								this.org.cancellationDate = new Date();
								this.org.dateEnforcementExhausted = null;
								if (this.org.history == null) {
									this.org.history = [];
								}
								let fauxExhaustedEventIndex = this.org.history.findIndex(h => h.id == '' && h.eventType == OrganizationEventType.EnforcementExhausted);
								if (fauxExhaustedEventIndex != -1) {
									this.org.history.splice(fauxExhaustedEventIndex, 1);
								}
								let fauxCancelledEvent = new OrganizationEvent();
								fauxCancelledEvent.id = '';
								fauxCancelledEvent.timeStamp = this.org.cancellationDate;
								fauxCancelledEvent.eventType = OrganizationEventType.OrganizationCancelled;
								fauxCancelledEvent.creatorContactId = this.auth.User.id;
								fauxCancelledEvent.contact = new Contact();
								fauxCancelledEvent.contact.id = this.auth.User.id;
								fauxCancelledEvent.contact.displayName = this.auth.User.displayName;
								fauxCancelledEvent.contact.organizationId = this.auth.User.organizationId;
								this.org.history.unshift(fauxCancelledEvent);
								break;
							}
							case "D": {
								this.org.cancellationDate = null;
								this.org.dateEnforcementExhausted = new Date();
								if (this.org.history == null) {
									this.org.history = [];
								}
								let fauxCancelledEventIndex = this.org.history.findIndex(h => h.id == '' && h.eventType == OrganizationEventType.OrganizationCancelled);
								if (fauxCancelledEventIndex != -1) {
									this.org.history.splice(fauxCancelledEventIndex, 1);
								}
								let fauxExhaustedEvent = new OrganizationEvent();
								fauxExhaustedEvent.id = '';
								fauxExhaustedEvent.timeStamp = this.org.dateEnforcementExhausted;
								fauxExhaustedEvent.eventType = OrganizationEventType.EnforcementExhausted;
								fauxExhaustedEvent.creatorContactId = this.auth.User.id;
								fauxExhaustedEvent.contact = new Contact();
								fauxExhaustedEvent.contact.id = this.auth.User.id;
								fauxExhaustedEvent.contact.displayName = this.auth.User.displayName;
								fauxExhaustedEvent.contact.organizationId = this.auth.User.organizationId;
								this.org.history.unshift(fauxExhaustedEvent);
								break;
							}
							default: {
								this.org.cancellationDate = null;
								this.org.dateEnforcementExhausted = null;
								break;
							}
						}
					}
					ctrl = this.editForm.get('isActive');
					if (ctrl != null) {
						this.org.isActive = ctrl.value;
						ctrl.disable();
					}
					break;
				}
			case "primaryPhone":
				{
					if (ctrl != null) {
						this.org.phone = ctrl.value;
						if (this.org.phone.length == 0) {
							this.org.noPhone = true;
						}
						ctrl.disable();
					}
					break;
				}
			case "fax":
				{
					if (ctrl != null) {
						this.org.fax = ctrl.value;
						ctrl.disable;
					}
					break;
				}
			case "emailAddress":
				{
					if (ctrl != null) {
						this.org.email = ctrl.value;
					}
					break;
				}
		}
		this._fieldUnderEdit = "";
	}

	updateContact(updated: Contact): void {
		let index = this.updatedContacts.findIndex(c => c.id == updated.id);
		if (index == -1) {
			this.updatedContacts.push(updated);
			if (this.primaryContact != null && (this.primaryContact.id === updated.id || updated.isPrimaryContact)) { //should ever only be one primary contact
				this.primaryContact = updated;
			}
			if (this.emergencyContact != null && this.emergencyContact.id === updated.id) {
				this.emergencyContact = updated;
			}
		} else {
			this.updatedContacts.splice(index, 1, updated);
		}
		
	}

    viewEDiscovery() : void {
        this.router.navigate(["admin", "eDiscovery", "org", this.org.id]);
    }
}
